import React from 'react';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  return (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Japanese lantern/chochin style */}
                  <ellipse cx="24" cy="14" rx="10" ry="4" fill="black"/>
                  <rect x="14" y="14" width="20" height="20" rx="10" fill="black"/>
                  <path d="M16 18 L32 18 M16 24 L32 24 M16 30 L32 30" stroke="white" strokeWidth="2"/>
                  <ellipse cx="24" cy="34" rx="10" ry="4" fill="black"/>
                  {/* Center vertical line */}
                  <rect x="23" y="10" width="2" height="28" fill="black"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-xl tracking-tight">MIKADO</span>
                <span className="text-gray-400 text-xs tracking-widest">SUSHI</span>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('footerDescription')}
            </p>
            <div className="flex space-x-4">
              <a
                href="#"
                className="w-10 h-10 bg-white/10 hover:bg-white hover:text-black rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 hover:bg-white hover:text-black rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 hover:bg-white hover:text-black rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t('quickLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {t('home')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {t('menu')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {t('about')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('delivery')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {t('delivery')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t('contactInfo')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <a href="tel:+37128717331" className="text-gray-400 hover:text-white transition-colors duration-200">
                  +371 28717331
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <a href="mailto:ordersmikado@gmail.com" className="text-gray-400 hover:text-white transition-colors duration-200">
                  ordersmikado@gmail.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-400">
                  Liepu iela 9, Liepāja<br />
                  Latvia
                </span>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t('businessHours')}</h3>
            <ul className="space-y-2">
              <li className="flex justify-between text-gray-400">
                <span>{t('monday')}</span>
                <span className="font-medium text-red-400">{t('closed')}</span>
              </li>
              <li className="flex justify-between text-gray-400">
                <span>{t('tueThu')}</span>
                <span className="font-medium">12:00 - 21:00</span>
              </li>
              <li className="flex justify-between text-gray-400">
                <span>{t('friSat')}</span>
                <span className="font-medium">12:00 - 22:00</span>
              </li>
              <li className="flex justify-between text-gray-400">
                <span>{t('sunday')}</span>
                <span className="font-medium">12:00 - 21:00</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} Mikado Sushi. {t('allRightsReserved')}
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors duration-200">
                {t('privacyPolicy')}
              </a>
              <a href="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors duration-200">
                {t('termsOfService')}
              </a>
              <a href="/company-details" className="text-gray-400 hover:text-white transition-colors duration-200">
                {t('companyDetailsTitle')}
              </a>
              <a href="/allergens" className="text-gray-400 hover:text-white transition-colors duration-200">
                {t('allergensTitle')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;